import { useState, useEffect, useCallback } from 'react';
import {
  WorkerInfo,
  Answer,
  CategoryType,
  CategoryGroup,
  CreateAnswerDto,
} from '@/types/workerInfo.types';
import { workerInfoService } from '@/api/wizard/workerInfo.service';

const CATEGORY_CONFIG: Record<CategoryType, { label: string; icon: string; description: string }> = {
  personal: {
    label: 'Informacion personal',
    icon: '👤',
    description: 'Datos basicos y preferencias laborales',
  },
  experience: {
    label: 'Experiencia laboral',
    icon: '💼',
    description: 'Historial profesional y logros',
  },
  education: {
    label: 'Formacion academica',
    icon: '🎓',
    description: 'Estudios y certificaciones',
  },
  skills: {
    label: 'Habilidades tecnicas',
    icon: '⚡',
    description: 'Tecnologias y competencias',
  },
  documentation: {
    label: 'Documentacion',
    icon: '📄',
    description: 'Documentos de soporte solicitados',
  },
};

function isAnswerCompleted(answer: Answer) {
  if (answer.textValue !== null && answer.textValue !== '') return true;
  if (answer.numberValue !== null) return true;
  if (answer.booleanValue !== null) return true;
  if (answer.dateValue !== null) return true;
  if (answer.arrayValue && answer.arrayValue.length > 0) return true;
  if (answer.experienceValue && answer.experienceValue.length > 0) return true;
  if (answer.educationValue && answer.educationValue.length > 0) return true;
  if (answer.skillValue && answer.skillValue.length > 0) return true;
  if (answer.documentValue && answer.documentValue.length > 0) return true;
  return false;
}

export function useWorkerInfo(workerId: string) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workerInfo, setWorkerInfo] = useState<WorkerInfo | null>(null);
  const [categories, setCategories] = useState<CategoryGroup[]>([]);
  const [activeCategory, setActiveCategory] = useState<CategoryType>('personal');

  const groupAnswersByCategory = useCallback((answers: Answer[]): CategoryGroup[] => {
    const groups: Record<CategoryType, Answer[]> = {
      personal: [],
      experience: [],
      education: [],
      skills: [],
      documentation: [],
    };

    answers.forEach((answer) => {
      const category = answer.question.category;
      if (category in groups) {
        groups[category as CategoryType].push(answer);
      }
    });

    Object.keys(groups).forEach((key) => {
      groups[key as CategoryType].sort((a, b) => a.question.order - b.question.order);
    });

    return (Object.keys(groups) as CategoryType[])
      .map((category) => ({
        category,
        ...CATEGORY_CONFIG[category],
        answers: groups[category],
      }))
      .filter((group) => group.answers.length > 0);
  }, []);

  const loadWorkerInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await workerInfoService.getWorkerInfo(workerId);
      if (response.success) {
        const grouped = groupAnswersByCategory(response.data.answers);
        setWorkerInfo(response.data);
        setCategories(grouped);

        if (grouped.length > 0) {
          setActiveCategory((current) =>
            grouped.some((c) => c.category === current) ? current : grouped[0].category
          );
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar la informacion');
    } finally {
      setLoading(false);
    }
  }, [groupAnswersByCategory, workerId]);

  const updateAnswer = useCallback(
    async (questionId: string, data: Partial<CreateAnswerDto>) => {
      if (!workerInfo) return;

      try {
        setSaving(true);
        const dto: CreateAnswerDto = { questionId, ...data };
        await workerInfoService.createOrUpdateAnswer(dto);

        setWorkerInfo((prev) => {
          if (!prev) return prev;

          const updatedAnswers = prev.answers.map((answer) =>
            answer.questionId === questionId
              ? {
                  ...answer,
                  ...data,
                  updatedAt: new Date().toISOString(),
                }
              : answer
          );

          setCategories(groupAnswersByCategory(updatedAnswers));
          return {
            ...prev,
            answers: updatedAnswers,
          };
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error al actualizar');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [groupAnswersByCategory, workerInfo]
  );

  useEffect(() => {
    loadWorkerInfo();
  }, [loadWorkerInfo]);

  const getCategoryAnswers = useCallback(
    (category: CategoryType) => categories.find((c) => c.category === category)?.answers || [],
    [categories]
  );

  const changeCategory = useCallback((category: CategoryType) => {
    setActiveCategory(category);
  }, []);

  const getCategoryProgress = useCallback(
    (category: CategoryType) => {
      const categoryGroup = categories.find((c) => c.category === category);
      if (!categoryGroup) return 0;

      const totalQuestions = categoryGroup.answers.length;
      const answeredQuestions = categoryGroup.answers.filter(isAnswerCompleted).length;

      return totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
    },
    [categories]
  );

  return {
    loading,
    saving,
    error,
    workerInfo,
    categories,
    activeCategory,
    getCategoryAnswers,
    changeCategory,
    updateAnswer,
    getCategoryProgress,
    refresh: loadWorkerInfo,
  };
}
